import { useMemo, useState } from 'react';
import { useForm } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import { useTranslation } from 'react-i18next';
import { Box, HStack, Input, Separator, Stack, Text } from '@chakra-ui/react';
import { Button } from '@/shared/ui/chakra/button';
import { Checkbox } from '@/shared/ui/chakra/checkbox';
import { Field } from '@/shared/ui/chakra/field';
import { ApiError } from '@/shared/api/client';
import { selectIsAdmin, useAuthStore } from '@/shared/lib/auth/store';
import { adminLogin, clientLogin } from '../api/login';
import { endpointHintFor, loadRememberedUsername, rememberLogin } from '../model/loginHint';
import type { LoginEndpoint } from '../model/loginHint';
import { fetchMe } from '../api/me';
import { useWebAuthnLogin } from '../model/useWebAuthnLogin';
import { loginSchema } from '../model/schemas';
import type { LoginFormValues } from '../model/schemas';
import { WebauthnRegisterPrompt } from './WebauthnRegisterPrompt';

interface Props {
  onSuccess: () => void;
}

/* "Remember login" — stores ONLY the username (never the password) so
   it pre-fills the field next time the login screen is actually shown.
   Staying logged in is handled securely by the HttpOnly refresh cookie
   (30-day session, auto-exchanged for an access token on each load), so
   no secret is ever kept at rest in the browser.

   Рядом лежит дверь, которой этот логин входил в прошлый раз — чтобы одно нажатие с неверным
   паролем не стоило двух неудачных попыток на бэкенде (см. `model/loginHint`). */

/* Вход только логином и паролем. Поле кода 2FA убрано 18.09.2026 по решению владельца:
   2FA у админов выключена, и форма её больше не предлагает. */
export function LoginForm({ onSuccess }: Props) {
  const { t } = useTranslation();
  const setAccessToken = useAuthStore((s) => s.setAccessToken);
  const setUser = useAuthStore((s) => s.setUser);
  const setStatus = useAuthStore((s) => s.setStatus);

  const rememberedUsername = useMemo(loadRememberedUsername, []);
  const [remember, setRemember] = useState(rememberedUsername !== null);
  const [submitError, setSubmitError] = useState<string | null>(null);
  // Set once a password login for an admin account succeeds — holds navigation until the
  // register-a-passkey offer (WebauthnRegisterPrompt) resolves one way or another.
  const [offerWebauthnRegister, setOfferWebauthnRegister] = useState(false);

  const webauthn = useWebAuthnLogin((resp) => {
    setAccessToken(resp.access_token);
    void fetchMe().then((me) => {
      setUser(me);
      setStatus('authenticated');
      onSuccess();
    });
  });

  const form = useForm<LoginFormValues>({
    resolver: zodResolver(loginSchema),
    mode: 'onSubmit',
    defaultValues: {
      username: rememberedUsername ?? '',
      password: '',
    },
  });
  const { errors } = form.formState;

  // Shared success path for either endpoint. Persist only the username
  // (never the password) — staying logged in is the refresh cookie's job.
  const finishLogin = async (accessToken: string, username: string, endpoint: LoginEndpoint) => {
    rememberLogin(username, endpoint, remember);
    setAccessToken(accessToken);
    const me = await fetchMe();
    setUser(me);
    setStatus('authenticated');
    // Only admins can register a passkey (backend gates register-options on AuthedAs<Admin>) —
    // a provider account goes straight through, same as before this feature existed.
    if (selectIsAdmin(me.role)) {
      setOfferWebauthnRegister(true);
      return;
    }
    onSuccess();
  };

  const submit = form.handleSubmit(async (values) => {
    setSubmitError(null);
    // Этот логин уже входил — значит, дверь известна, и вторую дёргать незачем: на бэкенде
    // каждый запрос с неверным паролем тратит попытку из общего суточного бюджета.
    const hint = endpointHintFor(values.username);
    if (hint === 'client') {
      try {
        const resp = await clientLogin({ username: values.username, password: values.password });
        await finishLogin(resp.access_token, values.username, 'client');
      } catch (err) {
        setSubmitError(translateAuthCode(err instanceof ApiError ? err.code : undefined, t));
      }
      return;
    }
    try {
      const resp = await adminLogin(values);
      await finishLogin(resp.access_token, values.username, 'admin');
    } catch (err) {
      const apiErr = err instanceof ApiError ? err : null;
      const code = apiErr?.code;
      // A provider (client-cabinet) account is rejected by the admin
      // endpoint as invalid_credentials — retry the same credentials against
      // the client login before surfacing an error, so both roles share one
      // screen. Известный админский логин сюда не попадает — для него пробовать нечего.
      if (code === 'invalid_credentials' && hint !== 'admin') {
        try {
          const resp = await clientLogin({
            username: values.username,
            password: values.password,
          });
          await finishLogin(resp.access_token, values.username, 'client');
          return;
        } catch {
          setSubmitError(t('auth.error_invalid_credentials'));
          return;
        }
      }
      setSubmitError(translateAuthCode(code, t));
    }
  });

  return (
    <Stack gap="4" width="full">
      {webauthn.available ? (
        <>
          <Button
            type="button"
            variant="outline"
            width="full"
            loading={webauthn.busy}
            onClick={webauthn.login}
          >
            {t('webauthn.login_button')}
          </Button>
          {webauthn.error ? (
            <Box role="alert" color="red.fg" fontSize="sm">
              {webauthn.error}
            </Box>
          ) : null}
          <HStack gap="3" color="fg.muted">
            <Separator flex="1" />
            <Text fontSize="xs">{t('auth.or')}</Text>
            <Separator flex="1" />
          </HStack>
        </>
      ) : null}
      {/* The fixed 320px only fit inside the login card's own fixed 380px; now that the card
          itself is responsive (LoginPage.tsx), this just fills whatever width the card gives it. */}
      <form
        onSubmit={submit}
        noValidate
        aria-label={t('auth.login_title')}
        style={{ width: '100%' }}
      >
        <Stack gap="4">
          <Field
            label={t('auth.username')}
            invalid={Boolean(errors.username)}
            errorText={errors.username?.message}
          >
            <Input autoComplete="username" autoFocus {...form.register('username')} />
          </Field>
          <Field
            label={t('auth.password')}
            invalid={Boolean(errors.password)}
            errorText={errors.password?.message}
          >
            <Input type="password" autoComplete="current-password" {...form.register('password')} />
          </Field>
          <Checkbox checked={remember} onCheckedChange={(d) => setRemember(d.checked === true)}>
            {t('auth.remember_me')}
          </Checkbox>
          {submitError ? (
            <Box role="alert" color="red.fg" fontSize="sm">
              {submitError}
            </Box>
          ) : null}
          <Button type="submit" width="full" loading={form.formState.isSubmitting}>
            {t('auth.submit')}
          </Button>
        </Stack>
      </form>
      <WebauthnRegisterPrompt active={offerWebauthnRegister} onResolved={onSuccess} />
    </Stack>
  );
}

function translateAuthCode(code: string | undefined, t: (key: string) => string): string {
  switch (code) {
    case 'invalid_credentials':
      return t('auth.error_invalid_credentials');
    case 'rate_limited':
      return t('auth.error_rate_limited');
    case 'account_locked':
      return t('auth.error_account_locked');
  }
  return t('auth.error_network');
}
